import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/lib/auth";
import { isPinEnabled, setPinHash, clearPin } from "@/lib/pinLock";
import ReferralPanel from "@/components/ReferralPanel";
import SoundSettings from "@/components/SoundSettings";
import { Shield, Lock, Eye, EyeOff, Check } from "lucide-react";

export default function Settings() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [pinEnabled, setPinEnabled] = useState(false);
  const [stealth, setStealth] = useState(false);
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [showPin, setShowPin] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    setPinEnabled(isPinEnabled());
    setStealth(localStorage.getItem("pk2_stealth_mode") === "solitario");
  }, []);

  if (!user) return null;

  const canUseStealth = ["admin", "moderator", "companion", "agency"].includes(user.role);

  const savePin = async (e) => {
    e.preventDefault();
    setErr(""); setMsg("");
    if (!/^\d{4,6}$/.test(newPin)) {
      setErr(t("settings.pin_invalid"));
      return;
    }
    if (newPin !== confirmPin) {
      setErr(t("settings.pin_mismatch"));
      return;
    }
    await setPinHash(newPin);
    setPinEnabled(true);
    setNewPin(""); setConfirmPin("");
    setMsg(t("settings.pin_saved"));
  };

  const disablePin = () => {
    if (!window.confirm(t("settings.pin_remove_confirm"))) return;
    clearPin();
    setPinEnabled(false);
    setMsg(t("settings.pin_removed"));
  };

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-12 pt-16 pb-24" data-testid="settings-page">
      <div className="overline mb-3">{t("settings.overline")}</div>
      <h1 className="font-serif text-5xl font-light mb-3" style={{ color: "var(--text-primary)" }}>
        {t("settings.title_pre")} <em style={{ color: "var(--brand-primary)", fontStyle: "italic" }}>{t("settings.title_em")}</em>
      </h1>
      <p className="text-sm mb-10" style={{ color: "var(--text-secondary)" }}>
        {t("settings.subtitle")}
      </p>

      {/* Referrals section */}
      <section className="mb-6">
        <ReferralPanel />
      </section>

      {/* Sound settings */}
      <SoundSettings />

      {/* PIN section */}
      <section
        className="p-6 mb-6"
        style={{ background: "var(--bg-surface)", border: "1px solid var(--border-accent)" }}
        data-testid="settings-pin"
      >
        <div className="flex items-start gap-4 mb-5">
          <Lock size={20} strokeWidth={1.4} color="var(--brand-primary)" />
          <div className="flex-1">
            <h2 className="font-serif text-2xl mb-1" style={{ color: "var(--text-primary)" }}>
              {t("settings.pin_title")}
            </h2>
            <p className="text-xs" style={{ color: "var(--text-secondary)" }}>
              {pinEnabled ? t("settings.pin_active") : t("settings.pin_help")}
            </p>
            {pinEnabled && (
              <span
                className="inline-flex items-center gap-1 text-xs mt-2 px-2 py-1"
                style={{ background: "rgba(34,197,94,0.12)", color: "#22c55e", border: "1px solid #22c55e" }}
                data-testid="pin-active-tag"
              >
                <Check size={12} /> {t("settings.pin_active_tag")}
              </span>
            )}
          </div>
        </div>

        <form onSubmit={savePin} className="flex flex-col gap-3">
          <div className="relative">
            <input
              type={showPin ? "text" : "password"}
              inputMode="numeric"
              pattern="\d{4,6}"
              maxLength={6}
              placeholder={t("settings.pin_new")}
              value={newPin}
              onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ""))}
              className="input-field pr-12"
              autoComplete="new-password"
              data-testid="settings-pin-input"
            />
            <button
              type="button"
              onClick={() => setShowPin((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2"
              style={{ color: "var(--text-muted)" }}
              aria-label={showPin ? "Hide" : "Show"}
            >
              {showPin ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <input
            type={showPin ? "text" : "password"}
            inputMode="numeric"
            maxLength={6}
            placeholder={t("settings.pin_confirm")}
            value={confirmPin}
            onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ""))}
            className="input-field"
            data-testid="settings-pin-confirm"
          />
          <div className="flex gap-2">
            <button type="submit" className="btn-primary text-xs" data-testid="settings-pin-save">
              {t("settings.pin_save")}
            </button>
            {pinEnabled && (
              <button type="button" onClick={disablePin} className="btn-secondary text-xs" data-testid="settings-pin-remove">
                {t("settings.pin_remove")}
              </button>
            )}
          </div>
          {err && <p className="text-xs" style={{ color: "#ef6d6d" }} data-testid="settings-pin-error">{err}</p>}
          {msg && <p className="text-xs" style={{ color: "#22c55e" }} data-testid="settings-pin-msg">{msg}</p>}
        </form>
      </section>

      {/* Discreto section */}
      {canUseStealth && (
        <section
          className="p-6"
          style={{ background: "var(--bg-surface)", border: "1px solid var(--border-accent)" }}
          data-testid="settings-stealth"
        >
          <div className="flex items-start gap-4 mb-4">
            <Shield size={20} strokeWidth={1.4} color="var(--vip-gold)" />
            <div className="flex-1">
              <h2 className="font-serif text-2xl mb-1" style={{ color: "var(--text-primary)" }}>
                {t("settings.stealth_title")}
              </h2>
              <p className="text-xs" style={{ color: "var(--text-secondary)" }}>
                {t("settings.stealth_desc")}
              </p>
              {stealth && (
                <span
                  className="inline-flex items-center gap-1 text-xs mt-2 px-2 py-1"
                  style={{ background: "rgba(212,175,55,0.12)", color: "var(--vip-gold)", border: "1px solid var(--vip-gold)" }}
                  data-testid="stealth-active-tag"
                >
                  <Check size={12} /> {t("settings.stealth_active")}
                </span>
              )}
            </div>
          </div>

          <Link to="/discreto" className="btn-primary text-xs inline-block" data-testid="settings-stealth-open">
            {t("settings.stealth_choose")}
          </Link>
        </section>
      )}
    </div>
  );
}
